import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  List,
  ListItem,
  Collapse,
  ListItemText,
  Checkbox,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  CheckboxIcon,
  CheckboxOutlinedIcon,
} from '../../rsmCoreComponents/components/icons'
import { UserManagementFilterConstants } from '../../store/userManagement/userManagementSlice'
import UserManagementFilterCategoryContainer from './UserManagementFilterCategoryContainer'

// Styled components following the established InvoiceFiltersCheckBoxes pattern
const FilterItem = styled(ListItem)(() => ({
  display: 'flex',
  alignItems: 'middle',
  padding: '0 0',
}))

const FilterList = styled(List)({
  border: 'none',
})

const style = {
  button: {
    height: 56,
    color: 'secondary.main',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0 0.375rem',
  },
  text: {
    color: 'secondary.main',
    fontWeight: 'medium',
    fontSize: '1.125rem',
    fontFamily: 'Prelo-Medium, sans-serif',
  },
}

// Interface for More/Less functionality
interface MoreLess {
  showMoreLess: boolean
  alwaysVisibleItems: string[]
  moreItems: string[]
  showMore: boolean
}

// Process items for See More/Less functionality
const processMoreLess = (
  filterOptions: string[],
  visibleItemsCount: number,
): MoreLess => {
  const showMoreLess = filterOptions.length > visibleItemsCount
  const alwaysVisibleItems = showMoreLess
    ? filterOptions.slice(0, visibleItemsCount)
    : filterOptions
  const moreItems = showMoreLess
    ? filterOptions.slice(visibleItemsCount, filterOptions.length)
    : []

  return {
    showMoreLess,
    alwaysVisibleItems,
    moreItems,
    showMore: moreItems.length > 0,
  }
}

interface UserManagementCheckBoxesFilterProps {
  filterCategory: string
  filterOptions: string[]
  clearCategoryFilter: (category: string) => void
  changeFilterAndSearch: (filterOption: string, value: string) => void
  visibleItemsCount: number
  selectedFilters: string[]
}

/**
 * UserManagementCheckBoxesFilter renders individual filter groups with checkboxes
 * for the User Management interface. Supports multi-select tool filters and status
 * filters with "See more/See less" functionality for long lists.
 *
 * Features:
 * - Multi-select checkbox behavior for filter options
 * - See more/See less toggle for lists longer than visibleItemsCount
 * - Integration with Redux state management via toggleToolFilter/toggleStatusFilter
 * - Material-UI components for consistent styling
 * - Accessibility support with proper ARIA attributes and test IDs
 * - Internationalization support for all user-facing text
 * - Follows the established InvoiceFiltersCheckBoxes pattern
 *
 * @param filterCategory - The category of filters (tools or status)
 * @param filterOptions - Array of available filter options to display
 * @param clearCategoryFilter - Callback to clear all filters in this category
 * @param changeFilterAndSearch - Callback to handle filter state changes
 * @param visibleItemsCount - Number of items to show before "See more" (default: 5)
 * @param selectedFilters - Array of currently selected filter values
 */
const UserManagementCheckBoxesFilter: React.FC<
  UserManagementCheckBoxesFilterProps
> = ({
  filterCategory,
  filterOptions,
  clearCategoryFilter,
  changeFilterAndSearch,
  visibleItemsCount = 5,
  selectedFilters,
}) => {
  const { t } = useTranslation()

  const [moreLessData, setMoreLessData] = useState<MoreLess>({
    showMoreLess: false,
    alwaysVisibleItems: [],
    moreItems: [],
    showMore: false,
  })

  // Check if a specific filter option is currently selected
  const isFilterChecked = useCallback(
    (option: string): boolean => selectedFilters.includes(option),
    [selectedFilters],
  )

  // Handle the See More/See Less toggle
  const handleMoreLess = useCallback(() => {
    let moreLess: MoreLess = {
      showMoreLess: true,
      alwaysVisibleItems: filterOptions,
      moreItems: [],
      showMore: false,
    }

    if (!moreLessData.showMore) {
      moreLess = processMoreLess(filterOptions, visibleItemsCount)
    }
    setMoreLessData(moreLess)
  }, [filterOptions, visibleItemsCount, moreLessData])

  // Initialize More/Less data when filter options change
  useEffect(() => {
    const moreLess = processMoreLess(filterOptions, visibleItemsCount)
    setMoreLessData(moreLess)
  }, [filterOptions, visibleItemsCount])

  // Render individual checkbox filter items
  const getCheckBoxFilterItems = (items: string[]) =>
    items.map((option: string) => (
      <FilterItem key={option}>
        <Checkbox
          id={`${filterCategory}_${option}`}
          icon={<CheckboxOutlinedIcon />}
          checkedIcon={<CheckboxIcon />}
          checked={isFilterChecked(option)}
          onChange={() => {
            changeFilterAndSearch(filterCategory, option)
          }}
          data-testid={`Btn_UserManagement_Filter_${option.replace(
            /\s+/g,
            '_',
          )}`}
        />
        <ListItemText
          primary={
            filterCategory === UserManagementFilterConstants.tools
              ? t(
                  `UserManagement.FilterOptions.Tools.${option.replace(
                    /\s+/g,
                    '',
                  )}`,
                  option,
                )
              : t(`UserManagement.FilterOptions.Status.${option}`, option)
          }
        />
      </FilterItem>
    ))

  return (
    <UserManagementFilterCategoryContainer
      filterCategory={filterCategory}
      clearCategoryFilter={clearCategoryFilter}>
      <FilterList>
        {getCheckBoxFilterItems(moreLessData.alwaysVisibleItems)}
      </FilterList>
      <Collapse in={!moreLessData.showMore} timeout="auto" unmountOnExit>
        {getCheckBoxFilterItems(moreLessData.moreItems)}
      </Collapse>
      {moreLessData.showMoreLess && (
        <FilterItem>
          <Button
            sx={style.button}
            data-testid={
              moreLessData.showMore
                ? `Btn_UserManagement_SeeMore_${filterCategory}`
                : `Btn_UserManagement_SeeLess_${filterCategory}`
            }
            onClick={handleMoreLess}
            aria-live="polite">
            <span style={style.text}>
              {moreLessData.showMore
                ? t('UserManagement.SeeMore')
                : t('UserManagement.SeeLess')}
            </span>
          </Button>
        </FilterItem>
      )}
    </UserManagementFilterCategoryContainer>
  )
}

export default UserManagementCheckBoxesFilter
